import React, { useContext, useMemo } from 'react';
import * as PropTypes from 'prop-types';
import styled from 'styled-components';
import { Card, Divider, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import {
  eventNames,
  propertyNames,
  rightRailPropertyValues
} from '../../../../../constants/mixpanel';
import { spacingInRems } from '../../../../theme/themeUtils';
import { storeContext } from '../../../../context';
import PaymentItem from './PaymentItem/PaymentItem';
import OfferItem from './OfferItem/OfferItem';
import PaymentBalanceInfo from './PaymentBalanceInfo';
import MenuLinks from './MenuLinks';
import FinancialAssistance from './additionalInformation/FinancialAssistance';
import CustomProviderMessage from './additionalInformation/CustomProviderMessage';
import BillingQuestions from './additionalInformation/BillingQuestions';
import LanguageSelector from '../../../general/LanguageSelector';
import HelpAndLogout from '../../../Header/HelpAndLogout';
import PayableSelector from '../../../Header/PayableSelector';
import { observer } from 'mobx-react-lite';
import { Alert } from '@mui/material';
import { t } from '../../../../../utils/i18next';

const translationsBase = 'payables.payment_options';

const StyledContainer = styled.div`
  && {
    max-width: 34rem;
    position: relative; //for testing - remove later
    & > div > div {
      margin: ${spacingInRems(2)};
    }
    &.default {
      .card {
          border-radius: ${spacingInRems(0.6)};
          .alertStatus {
            white-space: break-spaces;
            align-items: center;
          }
        }
      }
    }
    &.drawer {
      .closeButton {
        position: absolute;
        right: 0;
        margin-right: 0.2rem;
        margin-top: 0.2rem;
        padding: 0.8rem;
      }
      .card {
        border-radius: 0;
        box-shadow: none;
        #helpAndLogout {
          padding: ${spacingInRems()};
          margin: 0;
        }
      }
    }
  }
`;

const Menu = observer(props => {
  const {
    tracking,
    payable,
    embeddedModel: { isEmbeddedPage },
    settings
  } = useContext(storeContext) || {};

  const { RIGHT_RAIL_SELECTION } = eventNames;
  const { SELECTION_NAME } = propertyNames;
  const {
    PAYMENT_SUMMARY, //payment history
    PAYMENT_PLAN_SUMMARY,
    MANAGE_PAYMENT_PLAN,
    VIEW_STATEMENTS,
    MANAGE_MY_NOTIFICATIONS,
    UPDATE_INFORMATION,
    FINANCIAL_ASSISTANCE,
    CUSTOM_MESSAGE
  } = rightRailPropertyValues;

  const { MAKE_A_PAYMENT, PAYMENT_PLAN } = PaymentItem.paymentTypes;

  const { displayType, closeClick } = props;
  const { DEFAULT, DRAWER } = Menu.displayTypes;

  const close = e => closeClick && closeClick();

  let hasOffer = payable?.displayOfferInBillsPage;
  let paymentButtonType = MAKE_A_PAYMENT;

  if (payable?.hasPaymentPlan) {
    paymentButtonType = PAYMENT_PLAN;
  }

  const paymemtPlanStatusAlert =
    payable?.hasPaymentPlan &&
    !payable?.hasActivePaymentPlan &&
    payable?.paymentPlan.status;

  const hideProviderName = useMemo(
    () => settings.get('billing_questions')?.hide_provider_name,
    [settings]
  );

  const updatePpMethod = settings.get(
    'pp_update_from_patient'
  )?.update_payment_plan_method;
  const onMenuClick = e => {
    let selectionName;
    switch (e.target.id) {
      case 'previous-payments':
        selectionName = PAYMENT_SUMMARY;
        break;
      case 'payment-plan-summary':
        selectionName = updatePpMethod
          ? MANAGE_PAYMENT_PLAN
          : PAYMENT_PLAN_SUMMARY;
        break;
      case 'view-statements':
        selectionName = VIEW_STATEMENTS;
        break;
      case 'manage-notifications':
        selectionName = MANAGE_MY_NOTIFICATIONS;
        break;
      case 'update-information':
        selectionName = UPDATE_INFORMATION;
        break;
      case 'financial-assistance':
        selectionName = FINANCIAL_ASSISTANCE;
        break;
      case 'custom-message':
        selectionName = CUSTOM_MESSAGE;
        break;
    }

    selectionName &&
      tracking.track(RIGHT_RAIL_SELECTION, { [SELECTION_NAME]: selectionName });
  };

  const getLanguageAndLogout = () => (
    <>
      <LanguageSelector>
        <Divider />
      </LanguageSelector>
      {displayType === DRAWER && !isEmbeddedPage && <HelpAndLogout />}
    </>
  );

  return (
    <StyledContainer className={displayType} onClickCapture={onMenuClick}>
      <Card className={'card'}>
        {payable && (
          <>
            {displayType === DEFAULT && (
              <>
                <PaymentItem
                  type={paymentButtonType}
                  buttonColor={hasOffer ? 'secondary' : 'primary'}
                />
                {paymemtPlanStatusAlert && (
                  <Alert
                    className="alertStatus"
                    severity={
                      payable?.paymentPlan.status === 'frozen'
                        ? 'warning'
                        : 'error'
                    }
                  >
                    {t(`${translationsBase}.message.${paymemtPlanStatusAlert}`)}
                  </Alert>
                )}
                <PaymentBalanceInfo />
                <Divider />
                {hasOffer && (
                  <>
                    <OfferItem />
                    <Divider />
                  </>
                )}
              </>
            )}
            {displayType === DRAWER && (
              <>
                <IconButton
                  aria-label="Close"
                  className="closeButton"
                  onClick={close}
                  size="large"
                >
                  <CloseIcon />
                </IconButton>

                <PayableSelector />
              </>
            )}

            <MenuLinks onClick={close} />
            <Divider />

            <CustomProviderMessage onClick={close}>
              <Divider />
            </CustomProviderMessage>

            <FinancialAssistance onClick={close}>
              <Divider />
            </FinancialAssistance>

            <BillingQuestions hideProviderName={hideProviderName} />
          </>
        )}
        {getLanguageAndLogout()}
      </Card>
    </StyledContainer>
  );
});

Menu.displayTypes = {
  DEFAULT: 'default',
  DRAWER: 'drawer'
};

Menu.propTypes = {
  displayType: PropTypes.string,
  closeClick: PropTypes.func
};

Menu.defaultProps = {
  displayType: Menu.displayTypes.DEFAULT
};

export default Menu;
